import { confirm } from "@inquirer/prompts";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import type {
  CascadeFailureDetails,
  DeleteBindingResponse,
} from "@m8t-stack/api-contract";
import { apiCall } from "../../lib/api-client.js";
import { ApiCallError } from "../../lib/errors.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import {
  colors,
  renderJson,
  resolveOutputMode,
} from "../../lib/output.js";

export class BindRemoveCommand extends M8tCommand {
  static paths = [["bind", "remove"]];
  static usage = Command.Usage({
    description:
      "Remove a binding (cascade-delete: unregister webhook, delete KV secret, delete conversation threads, delete row).",
    details:
      "Works on bindings of any status (active or orphaned). For orphan-only cleanup, use 'm8t bind cleanup' instead.",
    examples: [
      ["Remove with confirm", "$0 bind remove cmo-tg"],
      ["Remove non-interactively", "$0 bind remove cmo-tg --yes"],
    ],
  });

  bindingId = Option.String();
  yes = Option.Boolean("--yes", false);
  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    if (!this.yes && interactive) {
      const ok = await confirm({
        message: `Remove binding '${this.bindingId}'? This unregisters its webhook and deletes all conversation threads.`,
        default: false,
      });
      if (!ok) {
        this.context.stdout.write("cancelled — nothing removed.\n");
        return 2;
      }
    }

    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });

    try {
      const data = await apiCall<DeleteBindingResponse>(
        { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
        { method: "DELETE", path: `/api/bindings/${encodeURIComponent(this.bindingId)}` },
        (msg) => this.context.stderr.write(msg + "\n"),
      );

      if (mode === "json") {
        this.context.stdout.write(renderJson(data) + "\n");
        return 0;
      }

      this.context.stdout.write(
        `${colors.success("✓")} removed binding ${colors.field(data.deleted.bindingId)} (channel: ${data.deleted.channel}). KV secret deleted; ${data.deleted.conversationsCleared.toString()} conversation thread(s) cleared.\n`,
      );
      return 0;
    } catch (e) {
      // Inline rendering for cascade_partial_failure — needs structured details.partial.
      // This reason is intentionally NOT in the hint table because it requires
      // the structured details.partial shape rather than a simple string hint.
      if (e instanceof ApiCallError) {
        const details = (e.envelope.details as Record<string, unknown> | undefined) ?? {};
        if (details.reason === "cascade_partial_failure" && mode !== "json") {
          const partial = details.partial as CascadeFailureDetails | undefined;
          if (partial) {
            const completed = partial.completedSteps.join(", ") || "(none)";
            this.context.stderr.write(
              colors.error("error:") +
                ` cascade partial failure for binding '${partial.bindingId}':\n`,
            );
            this.context.stderr.write(`  completed: ${completed}\n`);
            this.context.stderr.write(
              `  failed at: ${partial.failedStep} — ${partial.originalMessage}\n`,
            );
            this.context.stderr.write(
              colors.hint("  hint:") +
                ` re-run 'm8t bind remove ${partial.bindingId}' to retry; each step is idempotent.\n`,
            );
            return 1;
          }
        }
      }
      throw e;
    }
  }
}
